/**
 * Autosave scheduler.
 *
 * Trailing debounce with a maximum wait, and suspended entirely while a pointer
 * gesture is in flight: writing a whole-board snapshot mid-drag is what makes a
 * canvas app feel sticky. The save happens on gesture end instead, so the worst
 * case loss is the max wait.
 */

import type { Board } from '../scene/types';
import { isQuotaError, type SceneStore } from './idb';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface SaveState {
  readonly status: SaveStatus;
  readonly lastSavedAt: number | null;
  readonly error: string | null;
  /** Set once the quota is exhausted. Autosave stops rather than retrying. */
  readonly quotaBlocked: boolean;
}

export const INITIAL_SAVE_STATE: SaveState = {
  status: 'idle',
  lastSavedAt: null,
  error: null,
  quotaBlocked: false,
};

const DEBOUNCE_MS = 500;
const MAX_WAIT_MS = 2000;
const MIRROR_KEY = 'slate-whiteboard:crash-mirror';
const MIRROR_LIMIT_BYTES = 1_000_000;

export interface Autosaver {
  schedule(board: Board): void;
  /** Save immediately if anything is pending. Safe to call repeatedly. */
  flush(): Promise<void>;
  /** Best-effort synchronous mirror for pagehide. Never load bearing. */
  mirror(): void;
  suspend(): void;
  resume(): void;
  dispose(): void;
}

export function createAutosaver(
  store: SceneStore,
  onState: (state: SaveState) => void,
): Autosaver {
  let state = INITIAL_SAVE_STATE;
  let pending: Board | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let firstQueuedAt = 0;
  let suspended = false;
  let inFlight: Promise<void> | null = null;
  let disposed = false;

  const emit = (patch: Partial<SaveState>) => {
    state = { ...state, ...patch };
    onState(state);
  };

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const save = async (): Promise<void> => {
    if (disposed || state.quotaBlocked) return;
    const board = pending;
    if (!board) return;
    pending = null;
    firstQueuedAt = 0;
    emit({ status: 'saving' });
    try {
      await store.saveBoard(board);
      if (disposed) return;
      emit({ status: 'saved', lastSavedAt: Date.now(), error: null });
      try {
        localStorage.removeItem(MIRROR_KEY);
      } catch {
        // Storage may be unavailable; the mirror is best effort by design.
      }
    } catch (error) {
      if (disposed) return;
      if (isQuotaError(error)) {
        // Do not retry in a loop. The UI offers an export instead.
        emit({
          status: 'error',
          quotaBlocked: true,
          error: 'This browser is out of storage space. Export a backup, then delete a board.',
        });
        return;
      }
      emit({
        status: 'error',
        error: 'Could not save to local storage. Your last change is only in this tab.',
      });
    }
  };

  const run = () => {
    clearTimer();
    if (suspended) return;
    inFlight = save().finally(() => {
      inFlight = null;
    });
  };

  return {
    schedule(board) {
      if (disposed || state.quotaBlocked) return;
      pending = board;
      const now = Date.now();
      if (firstQueuedAt === 0) firstQueuedAt = now;
      emit({ status: 'pending' });
      if (suspended) return;
      clearTimer();
      // Cap the delay so a continuous stream of edits still reaches disk.
      const remaining = Math.max(0, MAX_WAIT_MS - (now - firstQueuedAt));
      timer = setTimeout(run, Math.min(DEBOUNCE_MS, remaining));
    },
    async flush() {
      clearTimer();
      if (inFlight) await inFlight;
      if (pending) await save();
    },
    mirror() {
      if (!pending) return;
      try {
        const text = JSON.stringify(pending);
        // A write started in pagehide cannot be awaited, so this is a top-up for
        // crash recovery only.
        if (text.length <= MIRROR_LIMIT_BYTES) localStorage.setItem(MIRROR_KEY, text);
      } catch {
        // Quota or a blocked storage API. Nothing to do.
      }
    },
    suspend() {
      suspended = true;
      clearTimer();
    },
    resume() {
      suspended = false;
      if (pending) run();
    },
    dispose() {
      disposed = true;
      clearTimer();
    },
  };
}

/** A board mirrored during an interrupted session, if it is newer than storage. */
export function readCrashMirror(): Board | null {
  let text: string | null = null;
  try {
    text = localStorage.getItem(MIRROR_KEY);
  } catch {
    return null;
  }
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as Board;
    if (typeof parsed?.id !== 'string' || !Array.isArray(parsed.elements)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearCrashMirror(): void {
  try {
    localStorage.removeItem(MIRROR_KEY);
  } catch {
    // Nothing to do.
  }
}
